import React, { useEffect, useMemo, useRef, useState, lazy, Suspense, useCallback } from 'react'
import { updateProjectsStagesStatus, IProjectStage, ISortProject, handleUpdateCurrentActiveStepApi, Stagestep, findStepIndex, CurrentStageStep, findPrevStep, findNextStep } from '../../../modules/project'
import { Field, Form, Formik, FormikProps, getIn, setIn } from 'formik'
import { isEqual } from 'lodash'
import { KTIcon } from '../../../../_theme/helpers'
import NotFound from '../../apps/components/NotFound'
import { useSnackbar } from '../../../../_theme/context/SnackbarContext'
import { ApiResponse, IDocumentData } from '../../core/_models'
import { useNavigate } from 'react-router-dom'
import { getProjectStageLink } from '../../../../utils/config'
import SubmitConfirmationModal from '../../apps/components/SubmitConfirmationModal'
import { useLeftStepper } from '../../../../_theme/context/LeftStepperContext'
import StepChangeAlertModal from '../../apps/components/StepChangeAlertModal'
import { CircleDot, ClipboardPen, FileText, Activity, Shield, Send, Users, Handshake, DollarSign, Layers, TrendingUp, ChartColumn } from 'lucide-react'
import StepIconSidebar from './StepIconSidebar'
import DynamicSymbol from '../../apps/components/DynamicSymbol'
import ThemeTooltip from '../../apps/components/UI/ThemeTooltip'
import { IPrefeasibilityStageSchema, prefeasibilityStageSchema, stepTabSchemas, findIncompleteStepTab } from '../components/Prefeasibility-steps/core/_validation'
import { prefeasibilityDefaultValues } from '../components/Prefeasibility-steps/core/defaultValues'
import { updateProjectStatusAndReadiness, saveProjectStageThree, saveOptionJustifications, updateRiskAndQualityGate, updateCbaCeaIntelligence, updateSensitivityAnalysis, saveStepTab } from '../components/Prefeasibility-steps/core/_request'
import { IPrefeasibilityTypes, IProjectStatusAndReadiness, IStakeholderCommunicationReadiness, IImplementationReadiness, IRiskAndQualityGate, IProjectFeasibilityAndOptionsAnalysis, ICbaCeaIntelligenceEngine, IRiskAndSensitivityAnalysisEngine, STEP_TABS } from '../components/Prefeasibility-steps/core/_models'
import { toFormFields } from '../core/formFields'
import { updateImplementationReadiness, updateStakeholderCommunicationReadiness } from '../components/Prefeasibility-steps/core/_request'
import { updateProjectDocuments } from '../core/document/_request'
import { LogError } from '../../../../utils/GlobalLogging'
import { useQueryClient } from '@tanstack/react-query'
import { feasibilityReadinessQueryKey } from '../components/Prefeasibility-steps/core/_queries'



//  Lazy load all step components
const ProjectStatusAndReadiness = lazy(() => import('../components/Prefeasibility-steps/ProjectStatusAndReadiness').then(module => ({ default: module.ProjectStatusAndReadiness })));
import { getApiErrorMessage } from "../../../../utils/utils";
const ImplementationReadiness = lazy(() => import('../components/Prefeasibility-steps/ImplementationReadiness').then(module => ({ default: module.ImplementationReadiness })));
const StakeholderCommunicationReadiness = lazy(() => import('../components/Prefeasibility-steps/StakeholderCommunicationReadiness').then(module => ({ default: module.StakeholderCommunicationReadiness })));
const RiskAndQualityGateEngine = lazy(() => import('../components/Prefeasibility-steps/RiskAndQualityGateEngine').then(module => ({ default: module.RiskAndQualityGateEngine })));
const DocumentStep = lazy(() => import('../components/common/DocumentStep').then(module => ({ default: module.DocumentStep })));
const SubmitStep = lazy(() => import('../components/common/SubmitStep').then(module => ({ default: module.SubmitStep })));
const StageGateDecisionPanel = lazy(() => import('../components/Prefeasibility-steps/StageGateDecisionPanel').then(module => ({ default: module.StageGateDecisionPanel })));
const EconomicAndCommercialViability = lazy(() => import('../components/Prefeasibility-steps/EconomicAndCommercialViability').then(module => ({ default: module.EconomicAndCommercialViability })));
const FeasibilityOptionsAnalysis = lazy(() => import('../components/Prefeasibility-steps/FeasibilityOptionsAnalysis').then(module => ({ default: module.FeasibilityOptionsAnalysis })));
const DemandAndNeedsValidationEngine = lazy(() => import('../components/Prefeasibility-steps/DemandAndNeedsValidationEngine').then(module => ({ default: module.DemandAndNeedsValidationEngine })));
const CBACEAiIntelligenceEngine = lazy(() => import('../components/Prefeasibility-steps/CBACEAiIntelligenceEngine').then(module => ({ default: module.CBACEAiIntelligenceEngine })));
const RiskAndSensitivityAnalysisEngine = lazy(() => import('../components/Prefeasibility-steps/RiskAndSensitivityAnalysisEngine').then(module => ({ default: module.RiskAndSensitivityAnalysisEngine })));
type StepComponentProps = {
    submitLoader: boolean;
    project_id: string;
    disableFormFields: boolean;
    setInitValues: React.Dispatch<React.SetStateAction<IPrefeasibilityTypes>>;
    isApprover: boolean;
    stage: IProjectStage;
    handleProjectRequestSubmit: (stageStatus: string, remarks: string) => void;
    setIsAllowNextStep: React.Dispatch<React.SetStateAction<boolean>>
    setNextTabHandler: (handler: NextTabHandler | null) => void
    // The open tab of a tab-wise step (a STEP_TABS tab id).
    stepTab: number;
    setStepTab: (tabId: number) => void;
}

// A tabbed step's Save & Next: opens its next tab and returns true, or returns false on its last tab.
// Used by tabbed steps that save as a whole; tab-wise steps (STEP_TABS) are handled here instead.
type NextTabHandler = () => boolean;

const FIRST_STEP_TAB = 1;

type Props = {
    project: ISortProject;
    stage: IProjectStage;
    isApprover: boolean;
    user_id: string;
    readonlyAccess: boolean;
    canSubmit: boolean;
}

type StageSubmitData = {
    project_id: number;
    dk_stage: string;
}

type StageStatusUpdateData = StageSubmitData & {
    project_stage_id: string;
    action: string;
    remarks: string;
}

const baseStepperData: Stagestep[] = [
    {
        title: "Project Status & Readiness",
        key: "project_status_and_readiness",
        icon: <ClipboardPen size={22} />,
        iconColor: "text-success",
        iconBackground: "bg-light-success",
        components: ProjectStatusAndReadiness,
        showIndex: false,
    },
    {
        title: "Implementation Readiness",
        key: "implementation_readiness",
        icon: <Activity size={22} />,
        iconColor: "text-danger",
        iconBackground: "bg-light-danger",
        components: ImplementationReadiness,
        showIndex: false,
    },
    {
        title: "Stakeholder & Communication Readiness",
        key: "stakeholder_and_communication_readiness",
        icon: <Users size={22} />,
        iconColor: "text-info",
        iconBackground: "bg-light-info",
        components: StakeholderCommunicationReadiness,
        showIndex: false,
    },
    {
        title: "Risk & Quality Gate — Core Decision Engine",
        key: "risk_and_quality_gate",
        icon: <Shield size={22} />,
        iconColor: "text-primary",
        iconBackground: "bg-light-primary",
        components: RiskAndQualityGateEngine,
        showIndex: false,
    },
    {
        title: "Economic & Commercial Viability",
        key: "economic_and_commercial_viability",
        icon: <DollarSign size={22} />,
        iconColor: "text-warning",
        iconBackground: "bg-light-warning",
        components: EconomicAndCommercialViability,
        showIndex: false,
    },
    {
        title: "Options Identification & Screening Engine",
        key: "options_identification_and_screening",
        icon: <Layers size={22} />,
        iconColor: "text-warning",
        iconBackground: "bg-light-warning",
        components: FeasibilityOptionsAnalysis,
        showIndex: false,
    },
    {
        title: "Demand & Needs Validation Engine",
        key: "demand_and_needs_validation",
        icon: <TrendingUp size={22} />,
        iconColor: "text-primary",
        iconBackground: "bg-light-primary",
        components: DemandAndNeedsValidationEngine,
        showIndex: false,
        showContinue: true,
    },
    {
        title: "CBA / CEA Intelligence Engine",
        key: "cba_cea_intelligence",
        icon: <ChartColumn size={22} />,
        iconColor: "text-info",
        iconBackground: "bg-light-info",
        components: CBACEAiIntelligenceEngine,
        showIndex: false,
    },
    {
        title: "Risk & Sensitivity Analysis Engine",
        key: "risk_and_sensitivity_analysis",
        icon: <Shield size={22} />,
        iconColor: "text-danger",
        iconBackground: "bg-light-danger",
        components: RiskAndSensitivityAnalysisEngine,
        showIndex: false,
    },
    {
        title: "Documents",
        key: "documents",
        icon: <FileText size={22} />,
        iconColor: "text-primary",
        iconBackground: "bg-light-primary",
        components: DocumentStep,
        showIndex: false,
    },
    {
        title: "Stage Gate Decision Panel",
        key: "stage_gate_decision_panel",
        icon: <Handshake size={22} />,
        iconColor: "text-danger",
        iconBackground: "bg-light-danger",
        components: StageGateDecisionPanel,
        showIndex: false,
        showContinue: true,
    },
];

const Prefeasibility: React.FC<Props> = ({ user_id, project, stage, isApprover, readonlyAccess, canSubmit }) => {
    const { leftStepperHide } = useLeftStepper();
    const project_id = project.project_id;
    const { showSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const navigate = useNavigate();


    const stepperData: Stagestep[] = useMemo(() => {
        return [
            ...baseStepperData,
            {
                title: stage.send_to_approval === "yes" ? "Approval" : "Submit",
                key: "approval_submit",
                showIndex: false,
                icon: <Send size={22} />,
                iconColor: "text-primary",
                iconBackground: "bg-primary-light",
                description: stage.send_to_approval === "yes" ? "Submit the project for approval" : "Submit the project",
                components: SubmitStep
            }
        ];
    }, [stage.send_to_approval]);

    const [submitLoader, setSubmitLoader] = useState<boolean>(false);
    const [showSubmitConfirmationModal, setShowSubmitConfirmationModal] = useState(false);
    const [stepChangeAlertModal, setStepChangeAlertModal] = useState(false);
    const [pendingStep, setPendingStep] = useState<string | null>(null);
    const [validateOnStepChange, setValidateOnStepChange] = useState(false);
    const [isAllowNextStep, setIsAllowNextStep] = useState(true);
    const [currentStep, setCurrentStep] = useState<CurrentStageStep>(() => {
        const stored = stage.active_step?.toString() || null;
        const initialStep = findStepIndex(stepperData, stored || "", true)
        if (stored && initialStep.step.key !== stored) {
            console.warn(`Invalid step key: ${stored}`);
        }
        return initialStep;
    });

    const formikRef = useRef<FormikProps<IPrefeasibilityTypes>>(null);
    const nextTabHandlerRef = useRef<NextTabHandler | null>(null);

    const setNextTabHandler = useCallback((handler: NextTabHandler | null): void => {
        nextTabHandlerRef.current = handler;
    }, []);

    // Opens the tab the user asked for while the current one had unsaved edits.
    const [pendingTabChange, setPendingTabChange] = useState<{ open: () => void } | null>(null);

    // The open tab of a tab-wise step (a STEP_TABS tab id), kept with the step it was opened on.
    // A newly opened step reads its first tab in the same render, so a tab left over from the
    // previous step never reaches it.
    const [openTab, setOpenTab] = useState<{ step: CurrentStageStep; tabId: number }>(
        () => ({ step: currentStep, tabId: FIRST_STEP_TAB })
    );
    const stepTab = openTab.step === currentStep ? openTab.tabId : FIRST_STEP_TAB;
    const setStepTab = useCallback((tabId: number) => {
        setOpenTab({ step: currentStep, tabId });
    }, [currentStep]);

    // The open step's tabs when it saves one tab at a time.
    const stepTabs = STEP_TABS[currentStep.step.key];
    const openTabFields = useMemo<readonly string[]>(
        () => stepTabs?.find((tab) => tab.tabId === stepTab)?.fields ?? [],
        [stepTabs, stepTab]
    );

    const [currentSchema, setCurrentSchema] = useState(() =>
        prefeasibilityStageSchema[stepperData[0].key as IPrefeasibilityStageSchema]
    );

    // Tab-wise steps validate only the open tab; a tab with no required fields has no schema.
    const validationSchema = useMemo(
        () => (stepTabs ? stepTabSchemas[currentStep.step.key]?.[stepTab] : currentSchema),
        [stepTabs, currentStep.step.key, stepTab, currentSchema]
    );

    const [initValues, setInitValues] = useState<IPrefeasibilityTypes>(
        prefeasibilityDefaultValues.project_status_and_readiness as IPrefeasibilityTypes
    );

    const disableFormFields = useMemo(() => {
        return isApprover || readonlyAccess || ["stage_status_3", "stage_status_4", "stage_status_6"].includes(stage.dk_stage_status);
    }, [isApprover, readonlyAccess, stage.dk_stage_status]);

    useEffect(() => {
        setCurrentSchema(prefeasibilityStageSchema[currentStep.step.key as IPrefeasibilityStageSchema]);
        setInitValues(prefeasibilityDefaultValues[currentStep.step.key as keyof typeof prefeasibilityDefaultValues] as IPrefeasibilityTypes);
        setIsAllowNextStep(currentStep.step?.isAllowNextStep === false ? false : true);
    }, [currentStep]);

    const handleUpdateActiveStep = useCallback(async (activeStep: string): Promise<void> => {
        const response = await handleUpdateCurrentActiveStepApi(stage.dk_stage, activeStep, Number(project_id)) as ApiResponse;
        if (response.status !== "ok") {
            showSnackbar(response.message, 'danger');
        }
    }, [stage.dk_stage, project_id, showSnackbar]);

    const confirmStepChange = useCallback((): void => {
        if (pendingStep) {
            setCurrentStep(findStepIndex(stepperData, pendingStep));
            setPendingStep(null);
        }
        setStepChangeAlertModal(false);
    }, [pendingStep, stepperData]);

    // Resolves true when the step was saved.
    const submitStep = useCallback(async (values: IPrefeasibilityTypes): Promise<boolean> => {

        // TODO: below code this when the API is ready
        if (isApprover) return false;

        setSubmitLoader(true);
        let projectStageResponse: ApiResponse = { status: "ok", message: "Data updated successfully", data: null };

        try {
            const stepKey = currentStep.step.key;
            const projectId = Number(project_id);

            const handlers: Record<string, () => Promise<ApiResponse>> = {
                project_status_and_readiness: async () => updateProjectStatusAndReadiness({ ...(values as IProjectStatusAndReadiness), project_id: projectId }),
                implementation_readiness: async () => {
                    const payload = values as IImplementationReadiness;
                    return updateImplementationReadiness({ ...payload, project_id: projectId });
                },
                stakeholder_and_communication_readiness: async () => {
                    const payload = values as IStakeholderCommunicationReadiness;
                    return updateStakeholderCommunicationReadiness({ ...payload, project_id: projectId });
                },
                risk_and_quality_gate: async () => updateRiskAndQualityGate({ ...(values as IRiskAndQualityGate), project_id: projectId }),
                // economic_and_commercial_viability is tab-wise: each tab saves through saveStepTab.
                options_identification_and_screening: async () => saveOptionJustifications({ ...(values as IProjectFeasibilityAndOptionsAnalysis), project_id: projectId }),
                cba_cea_intelligence: async () => updateCbaCeaIntelligence({ ...(values as ICbaCeaIntelligenceEngine), project_id: projectId }),
                risk_and_sensitivity_analysis: async () => updateSensitivityAnalysis({ ...(values as IRiskAndSensitivityAnalysisEngine), project_id: projectId }),
                documents: async () => updateProjectDocuments({ documents: (values as IDocumentData).documents, project_id: projectId, stage_id: stage.dk_stage }),

            };

            // A tab of a tab-wise step saves just that tab's fields to its sub-section.
            const openStepTab = stepTabs?.find((tab) => tab.tabId === stepTab);
            const sectionTab = openStepTab?.section ? { ...openStepTab, section: openStepTab.section } : undefined;
            const handler = sectionTab
                ? () => saveStepTab(projectId, sectionTab, values)
                : handlers[stepKey];

            if (!handler) {
                projectStageResponse = { status: "error", message: "Something went wrong", data: null };
            } else {
                projectStageResponse = await handler();
            }

            if (projectStageResponse.status === "ok") {
                showSnackbar(projectStageResponse.message, 'success');
                // The saved step can change the readiness shown in the header and the Stage Gate Decision Panel.
                queryClient.invalidateQueries({ queryKey: feasibilityReadinessQueryKey(projectId) });
                let activeStep = currentStep.step.key;
                const isSaveNext = values.formAction === "save-next";
                const openNextStep = () => {
                    const nextstep = findNextStep(currentStep, stepperData);
                    activeStep = nextstep.step.key;
                    setCurrentStep(nextstep);
                };

                if (stepTabs) {
                    // Only the saved tab comes back: mark it saved and keep the
                    // other tabs' values, including any unsaved edits.
                    const formik = formikRef.current;
                    let tabValues = values;
                    if (formik) {
                        const savedFields = projectStageResponse.data ? toFormFields(projectStageResponse.data, values) : {};
                        formik.resetForm({
                            values: { ...formik.initialValues, ...savedFields } as IPrefeasibilityTypes,
                        });
                        // formAction is not a saved value; left set, it would keep the form dirty.
                        tabValues = { ...values, ...savedFields, formAction: formik.initialValues.formAction } as IPrefeasibilityTypes;
                        void formik.setValues(tabValues, false);
                    }

                    // Save & Next opens the next tab; on the last tab it leaves the step only once
                    // every tab is complete, otherwise it opens the first incomplete tab with its errors.
                    const nextStepTab = stepTabs[stepTabs.findIndex((tab) => tab.tabId === stepTab) + 1];
                    if (isSaveNext && nextStepTab) {
                        setStepTab(nextStepTab.tabId);
                    } else if (isSaveNext) {
                        const incomplete = await findIncompleteStepTab(currentStep.step.key, tabValues);
                        if (incomplete) {
                            setStepTab(incomplete.tabId);
                            formik?.setErrors(incomplete.errors);
                            void formik?.setTouched(
                                Object.keys(incomplete.errors).reduce((acc, key) => setIn(acc, key, true), {}),
                                false,
                            );
                            showSnackbar(`Complete ${incomplete.title} before continuing.`, "warning");
                        } else {
                            openNextStep();
                        }
                    }
                } else {
                    // Save & Next opens the step's next tab, if it has one, before the next step.
                    const openedNextTab = isSaveNext && (nextTabHandlerRef.current?.() ?? false);

                    if (isSaveNext && !openedNextTab) {
                        openNextStep();
                    } else if (projectStageResponse.data && formikRef.current) {
                        // Same shape as a fresh load: the form's values with the saved ones over them.
                        const formik = formikRef.current;
                        formik.resetForm({
                            values: { ...formik.initialValues, ...toFormFields(projectStageResponse.data, values), formAction: formik.initialValues.formAction } as IPrefeasibilityTypes,
                        });
                    }
                }

                if (stage.active_step !== activeStep) {
                    await handleUpdateActiveStep(activeStep);
                }
                return true;
            } else {
                showSnackbar(projectStageResponse.message || "An error occurred while saving", 'danger');
            }
        } catch (error) {
            showSnackbar(getApiErrorMessage(error, "An error occurred while saving"), 'danger');
            LogError("An error occurred while saving", error, "Prefeasibility");

        } finally {
            setSubmitLoader(false);
        }
        return false;
    }, [isApprover, currentStep, project_id, stepTabs, stepTab, setStepTab, stepperData, stage.active_step, stage.dk_stage, handleUpdateActiveStep, showSnackbar, queryClient]);

    // Switching tabs in a tab-wise step asks first when the open tab has unsaved edits,
    // because Save only covers the open tab.
    const changeStepTab = useCallback((tabId: number) => {
        const formik = formikRef.current;
        if (!formik || tabId === stepTab) return;

        const hasUnsavedEdits = !disableFormFields && openTabFields.some(
            (field) => !isEqual(getIn(formik.values, field), getIn(formik.initialValues, field))
        );
        if (hasUnsavedEdits) {
            setPendingTabChange({ open: () => setStepTab(tabId) });
            return;
        }
        setStepTab(tabId);
    }, [stepTab, setStepTab, openTabFields, disableFormFields]);

    // Continue without saving: put the open tab back to its saved values, then switch.
    const discardAndChangeTab = useCallback(() => {
        const formik = formikRef.current;
        if (formik && pendingTabChange) {
            let restored = formik.values;
            openTabFields.forEach((field) => {
                restored = setIn(restored, field, getIn(formik.initialValues, field));
            });
            void formik.setValues(restored, false);
            formik.setErrors({});
            pendingTabChange.open();
        }
        setPendingTabChange(null);
    }, [openTabFields, pendingTabChange]);

    // Save & Continue: save the open tab (with its validation), and switch only if it saved.
    const saveAndChangeTab = useCallback(async () => {
        const formik = formikRef.current;
        const target = pendingTabChange;
        if (!formik || !target) return;

        await formik.setFieldValue("formAction", "save", false);
        // Typed as void, but resolves to submitStep's result, or undefined when validation fails.
        const saved: unknown = await formik.submitForm();
        setPendingTabChange(null);
        if (saved === true) {
            target.open();
        }
    }, [pendingTabChange]);

    const handleSaveAndContinue = useCallback(async (values: IPrefeasibilityTypes): Promise<void> => {
        try {
            await submitStep(values);
            if (pendingStep) {
                setCurrentStep(findStepIndex(stepperData, pendingStep));
                setPendingStep(null);
            }
        } catch (err) {
            showSnackbar(getApiErrorMessage(err, 'Something went wrong'), 'danger');
            LogError("Something went wrong", err, "Prefeasibility");
        } finally {
            setStepChangeAlertModal(false);
        }
    }, [pendingStep, stepperData, submitStep, showSnackbar]);

    const handleSubmitStage = useCallback((): void => {
        setShowSubmitConfirmationModal(true);
    }, []);

    useEffect(() => {
        if (!validateOnStepChange) return;

        const timeoutId = setTimeout(() => {
            if (formikRef.current) {
                formikRef.current.validateForm().then((errors) => {
                    const touchedFields = Object.keys(errors).reduce<Record<string, boolean>>((acc, key) => {
                        acc[key] = true;
                        return acc;
                    }, {});
                    formikRef.current?.setTouched(touchedFields);
                });
            }
        }, 100);

        setValidateOnStepChange(false);

        return () => clearTimeout(timeoutId);
    }, [initValues, validateOnStepChange]);

    const handleConfirmSubmitStage = useCallback(async (): Promise<void> => {
        setSubmitLoader(true);
        const data: StageSubmitData = { project_id: Number(project_id), dk_stage: stage.dk_stage };

        try {
            const stageSubmitResp = await saveProjectStageThree(data) as ApiResponse;
            if (stageSubmitResp.status === "ok") {
                // if (stage.send_to_approval === "no") {
                //     await getProjectReport(data, stage.dk_stage);
                // }
                showSnackbar(stageSubmitResp.message, 'success');
                navigate(getProjectStageLink(project_id));
            } else {
                showSnackbar(stageSubmitResp.message || 'Failed to submit stage', 'danger');
            }
        } catch (error) {
            showSnackbar(getApiErrorMessage(error, "Failed to submit stage"), 'danger');
            LogError("Failed to submit stage", error, "Prefeasibility");
        } finally {
            setSubmitLoader(false);
            setShowSubmitConfirmationModal(false);
        }
    }, [user_id, project_id, stage.dk_stage, stage.send_to_approval, navigate, showSnackbar]);

    const handleContinue = useCallback(() => {
        const nextStep = findNextStep(currentStep, stepperData);
        setCurrentStep(nextStep);
        handleUpdateActiveStep(nextStep.step.key);
    }, [currentStep, stepperData, handleUpdateActiveStep]);

    const prevStep = useCallback((dirty: boolean): void => {
        const prev = findPrevStep(currentStep, stepperData);
        const disabledStatuses = ["stage_status_3", "stage_status_4", "stage_status_6"];

        if (!disabledStatuses.includes(stage.dk_stage_status) && dirty) {
            setPendingStep(prev.step.key);
            setStepChangeAlertModal(true);
            return;
        }
        setCurrentStep(prev);
    }, [currentStep, stepperData, stage.dk_stage_status]);

    const goToStep = useCallback((key: string, dirty: boolean): void => {
        const disabledStatuses = ["stage_status_3", "stage_status_4", "stage_status_6"];

        if (!disabledStatuses.includes(stage.dk_stage_status) && dirty) {
            setPendingStep(key);
            setStepChangeAlertModal(true);
            return;
        }
        setCurrentStep(findStepIndex(stepperData, key));
    }, [stepperData, stage.dk_stage_status]);

    const handleProjectRequestSubmit = useCallback(async (stageStatus: string, remarks: string): Promise<void> => {
        setSubmitLoader(true);
        const data: StageStatusUpdateData = {
            project_id: Number(project_id),
            dk_stage: stage.dk_stage,
            project_stage_id: stage.project_stage_id,
            action: stageStatus,
            remarks
        };

        try {
            const stageSubmitResp = await updateProjectsStagesStatus(data) as ApiResponse;
            if (stageSubmitResp.status === "ok") {
                showSnackbar(stageSubmitResp.message, 'success');
                // if (stageStatus === stageCompletedCode) {
                //     await getProjectReport({ user_id, project_id, dk_stage: stage.dk_stage }, stage.dk_stage);
                // }
                navigate(`/dashboard`);
            } else {
                showSnackbar(stageSubmitResp.message, 'danger');
            }
        } catch (error) {
            showSnackbar(getApiErrorMessage(error, "Failed to update stage status"), 'danger');
            LogError("Failed to update stage status", error, "Prefeasibility");
        } finally {
            setSubmitLoader(false);
        }
    }, [user_id, project_id, stage.dk_stage, stage.project_stage_id, navigate, showSnackbar]);

    const loadStepper = useCallback((): React.ReactNode => {
        const CurrentStepComponent = currentStep.step.components;
        const StepComponent = CurrentStepComponent as React.ComponentType<StepComponentProps>;

        return (
            <Suspense fallback={<div className="p-4 text-center">Loading step...</div>}>
                <StepComponent
                    submitLoader={submitLoader}
                    project_id={project_id}
                    disableFormFields={disableFormFields}
                    setInitValues={setInitValues}
                    isApprover={isApprover}
                    stage={stage}
                    handleProjectRequestSubmit={handleProjectRequestSubmit}
                    setIsAllowNextStep={setIsAllowNextStep}
                    setNextTabHandler={setNextTabHandler}
                    stepTab={stepTab}
                    setStepTab={changeStepTab}
                />
            </Suspense>
        );
    }, [currentStep, submitLoader, project_id, disableFormFields, isApprover, stage, handleProjectRequestSubmit, setNextTabHandler, stepTab, changeStepTab]);

    const handleFormSubmit = useCallback(async (
        e: React.FormEvent<HTMLFormElement>,
        handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void,
        setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => Promise<void> | Promise<unknown>
    ) => {
        e.preventDefault();

        const submitter = (e.nativeEvent as SubmitEvent).submitter as HTMLButtonElement;
        const action = submitter?.value;

        if (action) {
            await setFieldValue("formAction", action);
        }

        handleSubmit(e);
    }, []);

    if (!initValues) return <NotFound />;

    return (
        <div className='stepper stepper-pills stepper-column d-flex flex-column flex-xl-row flex-row-fluid' id='project_stepper'>
            <Formik<IPrefeasibilityTypes>
                innerRef={formikRef}
                validationSchema={validationSchema}
                enableReinitialize={true}
                initialValues={initValues as IPrefeasibilityTypes}
                onSubmit={submitStep}
            >
                {({ handleSubmit, setFieldValue, dirty, values }) => (
                    <>
                        <div className={`card position-relative d-flex overflow-visible justify-content-center justify-content-xl-start flex-row-auto ${leftStepperHide ? 'w-100px' : 'w-xl-300px w-xxl-350px w-100'} me-9`}>
                            <StepIconSidebar
                                steps={stepperData}
                                currentStep={currentStep}
                                onStepClick={(key) => goToStep(key, dirty)}
                            />
                        </div>
                        <div className='d-flex flex-row-fluid justify-content-center bg-body rounded'>
                            <Form className='p-8 w-100 position-relative' noValidate id='kt_create_account_form'
                                onSubmit={(e) => handleFormSubmit(e, handleSubmit, setFieldValue)}>
                                <div className='card card-custom'>
                                    <div className='card-header align-items-center'>
                                        <div className='d-flex align-items-center'>
                                            <DynamicSymbol
                                                size={40}
                                                bgClass={`${currentStep.step.iconColor} ${currentStep.step.iconBackground}`}
                                                icon={currentStep.step.icon ? currentStep.step.icon : <CircleDot size={22} />}
                                            />
                                            <div>
                                                <div className='d-flex align-items-center'>
                                                    <h3 className='card-title align-items-center mb-0 mt-0'>{currentStep.step.title}</h3>
                                                    {currentStep.step.tooltip && <ThemeTooltip text={currentStep.step.tooltip} />}
                                                </div>
                                                {currentStep.step.description && <div className="text-muted fs-7 mt-0">{currentStep.step.description}</div>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className='card-body pt-2 px-10 pb-8'>
                                        {loadStepper()}
                                        {(!disableFormFields || (currentStep.isLast && canSubmit)) && (
                                            <div className='d-flex flex-stack pt-4 pb-0 px-4'>
                                                <div className='mr-2'>
                                                    {!currentStep.isFirst && (
                                                        <button onClick={() => prevStep(dirty)} type='button' className='btn btn-md btn-light-primary me-3'>
                                                            <KTIcon iconName='arrow-left' className='fs-4 me-1' />
                                                            Back
                                                        </button>
                                                    )}
                                                </div>
                                                <div>
                                                    {currentStep.step.showContinue ?
                                                    <> {isAllowNextStep && 
                                                        <button disabled={submitLoader} type='button' className='btn btn-md btn-primary ms-3' onClick={handleContinue}>
                                                            <span className='indicator-label d-flex flex-center'>
                                                                {'Next'}
                                                                <KTIcon iconName='arrow-right' className='fs-3 ms-2 me-0' />
                                                            </span>
                                                        </button> }
                                                        </>
                                                        : <>
                                                            {!currentStep.isLast ? (
                                                                <>
                                                                    <button disabled={submitLoader} type='submit' value='save' className='btn btn-md btn-active-primary btn-color-primary border border-primary btn-active-color-white save-only'>
                                                                        <span className='indicator-label d-flex flex-center'>{submitLoader && values.formAction === "save" ? <><span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span><span className='ms-2'>Saving...</span></> : "Save"}</span>
                                                                    </button>
                                                                    {isAllowNextStep && <button disabled={submitLoader} type='submit' value='save-next' className='btn btn-md btn-primary ms-3'>
                                                                        <span className='indicator-label d-flex flex-center'>
                                                                            {submitLoader && values.formAction === "save-next" ? <><span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span><span className='ms-2'>Saving & opening next...</span></> : "Save & Next"}
                                                                            <KTIcon iconName='arrow-right' className='fs-3 ms-2 me-0' />
                                                                        </span>
                                                                    </button>}
                                                                </>
                                                            ) : (
                                                                canSubmit && <button disabled={submitLoader} onClick={handleSubmitStage} type='button' className='btn btn-md btn-primary me-3'>
                                                                    <span className='indicator-label d-flex flex-center'>{submitLoader ? <><span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span><span className='ms-2'>Submitting...</span></> : "Submit"}</span>
                                                                </button>
                                                            )}
                                                        </>
                                                    }
                                                    <Field type="hidden" name="formAction" />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Form>
                            <StepChangeAlertModal
                                popupLoader={submitLoader}
                                show={stepChangeAlertModal}
                                onClose={() => setStepChangeAlertModal(false)}
                                onConfirm={confirmStepChange}
                                onSave={() => handleSaveAndContinue(values)}
                                message="Your updated data in this step will be lost if you move to another step without saving. Do you want to continue?"
                            />
                            <StepChangeAlertModal
                                popupLoader={submitLoader}
                                show={pendingTabChange !== null}
                                onClose={() => setPendingTabChange(null)}
                                onConfirm={discardAndChangeTab}
                                onSave={() => void saveAndChangeTab()}
                                message="Your changes in this tab are not saved. Save them before opening another tab, or continue and discard them."
                            />
                        </div>
                    </>
                )}
            </Formik>
            <SubmitConfirmationModal
                text="After submitting this stage, you will not be able to make any changes. Are you sure you want to submit this stage?"
                loader={submitLoader}
                show={showSubmitConfirmationModal}
                onClose={() => setShowSubmitConfirmationModal(false)}
                onConfirm={handleConfirmSubmitStage}
            />
        </div>
    )
}

export default Prefeasibility